// Kilometrage par semaine d'un decompte : prevu (seances du calendrier) et,
// a la demande, realise (sorties de course lues dans intervals.icu).
import { estCourse, type ActiviteRealisee } from '../Journal/journalApi.ts';
import { ajouterJours, iso } from './dates.ts';
import { dateLocale, lundiDe, semainesEntre } from './decompte.ts';
import type { Decompte, SeancePlanifiee } from './planningApi.ts';

export type KilometrageSemaine = {
  lundi: string;
  // n de Sn (S1 : semaine cible).
  n: number;
  // Distances en metres.
  prevu: number;
  // Realise, null si non demande ou semaine a venir.
  realise: number | null;
};

/**
 * Decompte en cours (couvrant la semaine du lundi donne), sinon le prochain
 * a venir, sinon null.
 */
export function decompteAffiche(decomptes: Decompte[], lundiCourant: string): Decompte | null {
  const enCours = decomptes.find((d) => d.lundiDebut <= lundiCourant && lundiCourant <= d.lundiCible);
  if (enCours) return enCours;
  return [...decomptes].filter((d) => d.lundiDebut > lundiCourant).sort((a, b) => a.lundiDebut.localeCompare(b.lundiDebut))[0] ?? null;
}

/** Lundis du decompte, de SN a S1. */
export function lundisDuDecompte(d: Decompte): string[] {
  const nb = semainesEntre(d.lundiDebut, d.lundiCible) + 1;
  return Array.from({ length: nb }, (_, i) => iso(ajouterJours(dateLocale(d.lundiDebut), 7 * i)));
}

/**
 * Kilometrage prevu par semaine du decompte ; realise (sorties de course)
 * seulement pour les semaines commencees quand des activites sont fournies.
 */
export function kilometragesParSemaine(
  d: Decompte,
  seances: SeancePlanifiee[],
  activites: ActiviteRealisee[] | null,
  aujourdhui: string,
): KilometrageSemaine[] {
  const semaines = lundisDuDecompte(d).map((lundi): KilometrageSemaine => ({
    lundi,
    n: semainesEntre(lundi, d.lundiCible) + 1,
    prevu: 0,
    realise: activites && lundi <= aujourdhui ? 0 : null,
  }));
  const parLundi = new Map(semaines.map((s) => [s.lundi, s]));
  const semaineDe = (date: string) => parLundi.get(iso(lundiDe(dateLocale(date))));
  seances.forEach((p) => {
    const s = semaineDe(p.date);
    if (s) s.prevu += p.distanceTotaleM ?? 0;
  });
  (activites ?? []).forEach((a) => {
    const s = semaineDe(a.date);
    if (!s || s.realise == null || a.date > aujourdhui || !estCourse(a)) return;
    s.realise += a.distanceM ?? 0;
  });
  return semaines;
}

/** Kilometres arrondis a l'unite, ex "42". */
export function km(m: number): string {
  return String(Math.round(m / 1000));
}
